import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { Building2, Inbox, Library, LineChart, LogOut, ShieldCheck } from 'lucide-react'
import { ThemeToggle } from './ThemeToggle'
import { useSession } from '../lib/session'

/**
 * Visibly not the school dashboard.
 *
 * The dark chrome is deliberate: this panel and the teacher dashboard share a
 * component library, and two near-identical windows where one can create schools is
 * an easy way to click the wrong thing.
 */
const nav = [
  { to: '/schools', label: 'Schools', icon: Building2, end: false },
  { to: '/curriculums', label: 'Curriculums', icon: Library, end: false },
  { to: '/waitlist', label: 'Waitlist', icon: Inbox, end: false },
  { to: '/platform-admins', label: 'Access', icon: ShieldCheck, end: false },
  { to: '/not-built', label: 'Not built yet', icon: LineChart, end: false },
]

export function Layout({ children }: { children: ReactNode }) {
  const { profile, signOut } = useSession()
  const name = profile?.display_name?.trim() || profile?.email || 'Signed in'

  return (
    <div className="relative min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-bg" />

      <div className="mx-auto flex min-h-screen max-w-[1500px] flex-col gap-4 p-4 md:flex-row md:gap-6 md:p-6">
        <aside className="glass-strong z-10 flex shrink-0 flex-col gap-6 rounded-3xl p-4 md:sticky md:top-6 md:h-[calc(100vh-3rem)] md:w-64">
          <div className="flex items-center gap-3 px-2 pt-2">
            <div className="brand-mark h-10 w-10" aria-hidden="true" />
            <div>
              <p className="text-base font-semibold leading-tight tracking-tight text-ink">
                Modulus
              </p>
              <p className="text-xs font-medium text-faint">Platform</p>
            </div>
          </div>

          <nav className="flex gap-1 md:flex-col">
            {nav.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex flex-1 items-center gap-3 rounded-2xl px-3.5 py-2.5 text-sm font-semibold transition md:flex-none ${
                    isActive
                      ? 'bg-accent-wash text-accent shadow-sm'
                      : 'text-muted hover:bg-raised hover:text-ink'
                  }`
                }
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto hidden md:block">
            <div className="mb-3">
              <ThemeToggle />
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-surface p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{name}</p>
                <p className="truncate text-xs text-faint">Platform admin</p>
              </div>
              <button
                onClick={() => void signOut()}
                title="Sign out"
                aria-label="Sign out"
                className="rounded-xl p-2 text-faint transition hover:bg-raised hover:text-ink"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 pb-10">{children}</main>
      </div>
    </div>
  )
}
